import { LoaderFunction, useRouteLoaderData } from "react-router-dom";
import { CoursesContextBrand } from "common/api/decimal/methods/course.ts";
import { Cart as StoreCart, getCart } from "common/api/decimal/methods/cart.ts";
import { getCoursesExtras } from "../../homeContext.ts";
import { loadBrandOnce } from "routes/root/rootLoader.ts";
import { buildNoCoursesResponse } from "../noCoursesResponse.ts";

declare global {
  interface Window {
    context: {
      token: string | null;
    };
  }
}

export interface MyCoursesConfig {
  allowPostDeadlineShopping: boolean;
  allowPreDeadlineShopping: boolean;
  assets: {
    showPrintPickup: boolean;
    labels: {
      library: string;
      oer: string;
      web: string;
    };
    notFoundHeader: string;
    notFound: string;
  };
  lms: {
    label: string;
  };
  personalizedValueSheet: {
    optingBannerButtonLabel: string | null;
  };
  showEposItems: boolean;
  showStudentChoiceCountdownBanner: boolean;
  sal: {
    contentAccessText: string | null;
    contentAccessUrl: string | null;
  };
  userSurvey: {
    show: boolean;
    url: string;
  };
  lateAddWarning: string | null;
}

export interface MyCoursesLayoutData {
  config: MyCoursesConfig;
  accessToken: string;
  storeCart?: StoreCart;
  isInstructor: boolean | null;
}

const getStoreCart = async () => {
  const brand = await loadBrandOnce();
  if (!brand.VirtualStore?.companyId) {
    return Promise.resolve(undefined);
  }
  return getCart().then(({ data }) => data);
};

const getBrandConfig = (brand: CoursesContextBrand | null) => {
  if (!brand) {
    return null;
  }
  return {
    allowPostDeadlineShopping: brand.allowPostDeadlineShopping,
    allowPreDeadlineShopping: brand.allowPreDeadlineShopping,
    assets: {
      showPrintPickup: !brand.printPickup.disabled,
      labels: {
        library: brand.assets.labels.library,
        oer: brand.assets.labels.oer,
        web: brand.assets.labels.web,
      },
      notFoundHeader: brand.assets.notFoundHeader,
      notFound: brand.assets.notFound,
    },
    lms: {
      label: brand.lms.label,
    },
    personalizedValueSheet: {
      optingBannerButtonLabel:
        brand.personalizedValueSheet.optingBannerButtonLabel?.trim() || null,
    },
    showEposItems: brand.showEposItems,
    showStudentChoiceCountdownBanner: brand.showStudentChoiceCountdownBanner,
    sal: brand.sal,
    userSurvey: {
      show: brand.userSurvey.showToast,
      url: brand.userSurvey.url,
    },
    lateAddWarning: brand.lateAddWarning,
  };
};

export function useLayoutData(): MyCoursesLayoutData {
  return useRouteLoaderData<MyCoursesLayoutData>("courses-root")!;
}

export const loadMyCoursesLayout: LoaderFunction = () => {
  // todo only load one cart based on brand flag?
  return Promise.all([getCoursesExtras(), getStoreCart()]).then(
    async ([context, storeCart]) => {
      if (!context.accessToken) {
        // Only an unmatched student (404) is helped by signing in. Other
        // failures are outages, where the generic message is the honest one.
        if (context.contextStatus === 404) {
          throw await buildNoCoursesResponse("Access Token Not Found");
        }
        throw new Response("Not Found", {
          status: 404,
          statusText: "Access Token Not Found",
        });
      }

      const config = getBrandConfig(context?.brand);
      if (!config) {
        throw new Response("Not Found", {
          status: 404,
          statusText: "Brand Not Found",
        });
      }
      const layoutData: MyCoursesLayoutData = {
        config,
        accessToken: context?.accessToken ?? "",
        storeCart: storeCart,
        isInstructor: context?.isInstructor,
      };
      return layoutData;
    }
  );
};
